"use client";

import { useEffect, useRef, useState } from "react";
import { Button, Input, Form, Checkbox } from "react-vant";
import ReCAPTCHA from "react-google-recaptcha";
import productApi from "@/service/modules/productApi";
import systemApi from "@/service/modules/systemApi";
import { toast } from "@/components/global/Toast";
import type { NotifyFormValues } from "@/types/product";
import { errorUtils } from "@/utils/error";
import Dialog from "@/components/global/Dialog";

interface Props {
  showNotify: boolean;
  productID: string;
  onClose: () => void;
}
export default function ProductNotifyDialog({
  productID,
  showNotify,
  onClose,
}: Props) {
  const [form] = Form.useForm();
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const [siteKey, setSiteKey] = useState("");
  const [btnLoading, setBtnLoading] = useState(false);
  const [newsletter, setNewsletter] = useState(true);

  useEffect(() => {
    if (!showNotify) return;

    let active = true;
    form.resetFields();
    recaptchaRef.current?.reset();

    const getRecaptchaSiteKey = async () => {
      try {
        const res = await systemApi.getRecaptchaSiteKey();
        if (active) {
          setSiteKey(res.data?.siteKey || "");
        }
      } catch (error: unknown) {
        if (active) {
          toast.show(errorUtils.getErrorMessage(error));
        }
      }
    };

    void getRecaptchaSiteKey();

    return () => {
      active = false;
    };
  }, [showNotify, form]);

  // 订阅checkbox触发器
  const onChangeNewsletter = (value: boolean) => {
    setNewsletter(value);
  };

  const onFinish = async (values: NotifyFormValues) => {
    if (btnLoading) return;

    const recaptcha = recaptchaRef.current?.getValue();
    if (!siteKey || !recaptcha) {
      toast.show("Please verify you are not a robot");
      return;
    }

    setBtnLoading(true);
    const params = {
      ...values,
      productID,
      recaptcha,
      newsletter,
    };
    try {
      await productApi.addNotify(params);
      form.resetFields();
      onClose();
      toast.show("Success");
    } catch (error: unknown) {
      recaptchaRef.current?.reset();
      toast.show(errorUtils.getErrorMessage(error));
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <Dialog
      visible={showNotify}
      title=""
      closeable={true}
      showConfirmButton={false}
      showCancelButton={false}
      onCancel={onClose}
    >
      <Form
        form={form}
        onFinish={onFinish}
        layout={"vertical"}
        footer={
          <div>
            <Button
              nativeType="submit"
              type="primary"
              style={{ fontWeight: "700" }}
              loading={btnLoading}
              loadingText="Send"
              block
            >
              Send
            </Button>

            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", paddingTop: "10px" }}>
              <Checkbox
                checked={newsletter}
                onChange={onChangeNewsletter}
                shape={"square"}
                iconSize={16}
                style={{
                  margin: "2px 10px 0 0",
                  flexShrink: 0,
                  fontSize: "13px",
                  lineHeight: "16px",
                }}
              ></Checkbox>

              <div style={{ fontSize: '13px', lineHeight: '16px' }}>
                I would like to receive your Newsletter to be the first to know
                about hot offers!
              </div>
            </div>
          </div>
        }
      >
        <div
          style={{
            paddingBottom: "14px",
            fontWeight: "bold",
            color: "#e6415f",
          }}
        >
          Notify me when the item is restock！
        </div>
        <Form.Item
          rules={[{ required: true, message: `Name is required` }]}
          name="userName"
          label="Name:"
        >
          <Input placeholder="Name" maxLength={225} />
        </Form.Item>

        <Form.Item
          rules={[
            { required: true, message: `Email Address is required` },
            { type: "email", message: `Invalid Email Address` },
          ]}
          name="email"
          label="Email address:"
        >
          <Input placeholder="Email address" maxLength={225} />
        </Form.Item>

        {!!siteKey && (
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={siteKey}
            type="audio"
          />
        )}
      </Form>
    </Dialog>
  );
}
